import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import { notify } from '../../lib/notifications'
import { Button } from '../ui/button'

export interface BlockingAsset {
    uid: string
    name: string
    documents: number
}

interface ImportBlockingApprovalProps {
    org: string
    blocking: BlockingAsset[]
}

/**
 * "N documents wait for the approval of <site>" with the button that approves
 * it (spec 2026-10-14-12 §3): approving the asset resumes the paused entity
 * extraction of its documents.
 */
export function ImportBlockingApproval({ org, blocking }: ImportBlockingApprovalProps) {
    const { t } = useTranslation('imports')
    const queryClient = useQueryClient()
    const [busy, setBusy] = useState<string | null>(null)

    const approve = async (uid: string) => {
        setBusy(uid)
        try {
            const { error } = await apiClient.POST('/api/v1/organizations/{orgCode}/assets/approve', {
                params: { path: { orgCode: org } },
                body: { uids: [uid], action: 'approve' },
            })
            if (error) throw new Error('approve failed')
            queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/organizations/{orgCode}/imports/{importUid}/summary'] })
            queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/organizations/{orgCode}/imports/{importUid}/proposals'] })
        } catch {
            notify({
                key: 'import-proposals-error',
                variant: 'destructive',
                titleKey: 'imports:proposals.error_title',
                descriptionKey: 'imports:proposals.error_body',
            })
        } finally {
            setBusy(null)
        }
    }

    if (blocking.length === 0) return null

    return (
        <ul className="mt-2 space-y-1" data-testid="import-pending-blockers">
            {blocking.map((b) => (
                <li key={b.uid} className="flex flex-wrap items-center justify-between gap-3" data-testid="import-pending-blocker">
                    <span data-testid="import-pending-blocker-text">
                        {t('pendingApproval.blocker', { count: b.documents, name: b.name })}
                    </span>
                    <Button
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => void approve(b.uid)}
                        data-testid={`import-pending-blocker-approve-${b.uid}`}
                    >
                        {t('pendingApproval.approve')}
                    </Button>
                </li>
            ))}
        </ul>
    )
}
